/**
 * Self-Referral Confirmation Component
 *
 * Displays the successful completion of the self-referral workflow.
 * Shows the matched dietitian and provides actions to continue to registration
 * or reset the referral process.
 *
 * @module components/self-referral/SelfReferralConfirmation
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';
import { PaymentGuidance } from '@/components/payment-guidance';
import type { MatchedDietitian } from './types';

export interface SelfReferralConfirmationProps {
  /** The selected dietitian information */
  selectedDietitian: MatchedDietitian;
  /** Callback when user clicks to continue to registration */
  onContinueToRegistration: () => void;
  /** Optional callback when user wants to create a new referral */
  onCreateNewReferral?: () => void;
  /** Optional custom class name */
  className?: string;
}

/**
 * Success icon component
 */
const SuccessIcon = () => (
  <svg
    className="mx-auto h-16 w-16 text-success-500 mb-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export function SelfReferralConfirmation({
  selectedDietitian,
  onContinueToRegistration,
  onCreateNewReferral,
  className,
}: SelfReferralConfirmationProps) {
  const { isDark } = useTheme();
  const dietitianName = selectedDietitian?.name || 'Unknown Dietitian';

  return (
    <div className={cn('text-center py-8', className)}>
      {/* Success icon */}
      <div className="mb-6">
        <SuccessIcon />
      </div>

      {/* Success message */}
      <p
        className={cn(
          'text-lg mb-6',
          isDark ? 'text-secondary-400' : 'text-secondary-600'
        )}
      >
        You've been matched with <strong>{dietitianName}</strong>.
        Complete your registration to schedule your first appointment.
      </p>

      {/* Fee guidance (design §4.6, §4.13.2) */}
      <div className="mb-6">
        <PaymentGuidance guidance={selectedDietitian.payment_guidance ?? null} />
      </div>

      {/* Action buttons */}
      <div className="space-y-3">
        <Button
          onClick={onContinueToRegistration}
          variant="primary"
          size="lg"
          className="w-full"
          data-testid="continue-to-registration-button"
        >
          Continue to Registration
        </Button>

        {onCreateNewReferral && (
          <Button
            onClick={onCreateNewReferral}
            variant="ghost"
            size="lg"
            className="w-full"
            data-testid="reset-referral-button"
          >
            Reset Referral
          </Button>
        )}
      </div>
    </div>
  );
}